import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { Card } from 'src/domains/players/webview/components/Account/common/card/Card';
import { BadgeGroup } from 'src/domains/players/webview/components/Account/common/badge/BadgeGroup';
import {
    CreditsCardButton,
    CreditsCardConditionsContainer,
    CreditsCardContainer,
    CreditsCardHeader,
    CreditsCardBadgeExpiryAt,
    CreditsCardRow,
    CreditsCardRowLabel,
    CreditsCardRowValue,
    CreditsCardSubHeader,
    ButtonsWrapper,
} from './Credits.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { Badge } from 'src/domains/players/webview/components/Account/common/badge/Badge';
import { BonusViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { CreditsState } from './Credits.state';
import { TermsPage } from 'src/domains/players/webview/components/SignUp';
import { TermsDrawerState } from './TermsDrawer/TermsDrawer.state';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { UsersState } from 'src/domains/players/state/UsersState';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';

interface CreditsBetSpinsCardPropsType {
    bonus: BonusViewType;
    creditsState: CreditsState;
}

export const CreditsBetSpinsCard = observer(
    'CreditsBetSpinsCard',
    ({ bonus, creditsState }: CreditsBetSpinsCardPropsType) => {
        const common = useCommon();
        const { customKeyboard } = AppPlayersState.get(common);
        const configState = ConfigComponents.get(common).config;
        const usersState = UsersState.get(common);

        const [state] = useState<TermsDrawerState>(() => new TermsDrawerState(common, customKeyboard, usersState));
        const campaignId = bonus.campaignId ?? null;
        const amount = bonus.status === 'active' ? bonus.currentAmount : bonus.totalAmount;

        return (
            <Card haveBorder={true}>
                {state.displayTermsPage ? (
                    <TermsPage
                        title='TERMS & CONDITIONS'
                        content={state.terms ?? 'Error occurred'}
                        closePage={state.closePage}
                        hasArrow
                    />
                ) : null}
                <CreditsCardContainer data-test={`credit-${bonus.bonusId}`}>
                    <div>
                        <CreditsCardHeader
                            data-test='credits-header'
                            variant={configState.componentVariants.accountSectionVariant}
                        >
                            <div>{creditsState.getCreditValue(amount ?? 0)}</div>
                            <div>
                                {bonus.status === 'active' ? (
                                    <CreditsCardBadgeExpiryAt>
                                        <I18n
                                            langKey='account.credits.badge.expires-at'
                                            defaultText='Expires'
                                        />{' '}
                                        {creditsState.formatExpires(bonus.expiryAt, 'badge')}
                                    </CreditsCardBadgeExpiryAt>
                                ) : (
                                    <Badge type={creditsState.mapBadgeTypeBasedOnStatus(bonus.status)}>
                                        {bonus.status}
                                    </Badge>
                                )}
                            </div>
                        </CreditsCardHeader>
                        {bonus.status === 'active' ? null : (
                            <CreditsCardSubHeader data-test='credits-expiry'>
                                {creditsState.formatExpires(bonus.expiryAt)}
                            </CreditsCardSubHeader>
                        )}
                    </div>
                    <CreditsCardConditionsContainer>
                        {creditsState.filterConditions(bonus)?.map((condition) => {
                            return (
                                <CreditsCardRow key={condition.type}>
                                    <CreditsCardRowLabel data-test={`credits-condition-${condition.type}-label`}>
                                        {condition.type}:
                                    </CreditsCardRowLabel>

                                    {typeof condition.value === 'string' ||
                                    typeof condition.value === 'number' ||
                                    typeof condition.value === 'boolean' ? (
                                        <CreditsCardRowValue data-test={`credits-condition-${condition.type}-value`}>
                                            {condition.value}
                                        </CreditsCardRowValue>
                                    ) : (
                                        <BadgeGroup badges={condition.value} />
                                    )}
                                </CreditsCardRow>
                            );
                        })}
                    </CreditsCardConditionsContainer>
                    {creditsState.creditType === 'free-bets' ? (
                        <ButtonsWrapper>
                            {campaignId === null ? null : (
                                <CreditsCardButton
                                    size='small'
                                    isLeft
                                    onClick={(): void => state.showTermsPage('free-bets', bonus.campaignId)}
                                    version='tertiary-dark'
                                    dataTest='view-terms-button'
                                >
                                    <I18n
                                        langKey='account.credits.view-terms'
                                        defaultText='View Terms'
                                    />
                                </CreditsCardButton>
                            )}

                            <CreditsCardButton
                                size='small'
                                onClick={creditsState.redirectToSportsbook}
                                disabled={creditsState.isButtonDisabled(bonus.status)}
                                dataTest='bet-now-button'
                            >
                                <I18n
                                    langKey='account.credits.bet-now'
                                    defaultText='Bet Now'
                                />
                            </CreditsCardButton>
                        </ButtonsWrapper>
                    ) : (
                        <ButtonsWrapper>
                            <CreditsCardButton
                                size='small'
                                isLeft
                                onClick={(): void => state.showTermsPage('free-spins', bonus.campaignId)}
                                version='tertiary-dark'
                                dataTest='view-terms-button'
                            >
                                <I18n
                                    langKey='account.credits.view-terms'
                                    defaultText='View Terms'
                                />
                            </CreditsCardButton>
                            <CreditsCardButton
                                size='small'
                                onClick={(): void => creditsState.redirectToCasino(bonus.provider ?? null)}
                                disabled={creditsState.isButtonDisabled(bonus.status)}
                                dataTest='play-now-button'
                            >
                                <I18n
                                    langKey='account.credits.play-now'
                                    defaultText='Play Now'
                                />
                            </CreditsCardButton>
                        </ButtonsWrapper>
                    )}
                </CreditsCardContainer>
            </Card>
        );
    }
);
